import { useEffect } from 'react';
import { useAppSelector } from '../store';
import { resumeSession, startSession, isSessionRunning, setSessionUnitStatus } from '../lib/mdtSession';
import { isSessionActive } from '../lib/mdtSessionStorage';
import type { Resource } from '../types';

type UnitStatus = 'available' | 'busy' | 'enroute' | 'onscene' | 'onbreak' | 'conveying';

interface UseMdtSessionOptions {
  mdtResource: Resource | null;
  unitStatus: UnitStatus;
  sessionReady: boolean;
}

/** Keeps GPS / push / wake lock in sync with the active MDT shift. */
export function useMdtSession({ mdtResource, unitStatus, sessionReady }: UseMdtSessionOptions): void {
  const { user, token } = useAppSelector((state) => state.auth);

  useEffect(() => {
    setSessionUnitStatus(unitStatus);
  }, [unitStatus]);

  useEffect(() => {
    if (!sessionReady || !mdtResource || !user || !token) return;

    const ctx = { resource: mdtResource, user, token };

    const boot = async () => {
      if (isSessionRunning()) return;
      if (isSessionActive()) {
        await resumeSession(ctx);
      } else {
        await startSession(ctx);
      }
    };

    boot();
  }, [sessionReady, mdtResource, user, token]);

  useEffect(() => {
    if (!sessionReady || !mdtResource || !user || !token) return;

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        resumeSession({ resource: mdtResource, user, token }).catch(() => {});
      }
    };

    const onAppResumed = () => {
      resumeSession({ resource: mdtResource, user, token }).catch(() => {});
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('mdt:app-resumed', onAppResumed);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('mdt:app-resumed', onAppResumed);
    };
  }, [sessionReady, mdtResource, user, token]);
}
